export const vueI18n = { instance: null };

const FLAG_BASE_URL = `https://eyeinlivestorage.blob.core.windows.net/public/icons/flags`;

function locale(locale, iso, name) {
    return {
        locale,
        shortLocale: locale,
        iso,
        name,
        flag: `${FLAG_BASE_URL}/${String(iso).toUpperCase()}.svg`
    };
}

// Define supported locales explicitly (single source of truth)
export const SUPPORTED_LOCALES = [
    locale(`en`, `US`, `English`),
    locale(`fr`, `FR`, `Français`),
    locale(`de`, `DE`, `Deutsch`),
    locale(`it`, `IT`, `Italiano`),
    locale(`es`, `ES`, `Español`),
    locale(`ca`, `ES`, `Castellano`),
    locale(`zh`, `CN`, `普通话`),
    locale(`ja`, `JP`, `日本語`),
    locale(`ko`, `KR`, `한국어`),
    locale(`ar`, `SA`, `العربية`)
];

const SUPPORTED_CODES = SUPPORTED_LOCALES.map(l => l.locale);
const PREVIEW_LANGUAGE_STORAGE_KEY = `previewLanguage`;
const FALLBACK_LANGUAGE_STORAGE_KEY = `fallbackLanguage`;
const LEGACY_LANGUAGE_STORAGE_KEY = `language`;
const I18NEXT_LANGUAGE_STORAGE_KEY = `i18nextLng`;

export const FALLBACK_LOCALE = `en`;
export let fallbackLocale = FALLBACK_LOCALE;

export function getFallbackLocale() {
    return fallbackLocale;
}

export function setFallbackLocale(locale) {
    const normalized = normalizeLocaleCode(locale) || FALLBACK_LOCALE;
    fallbackLocale = normalized;

    if (typeof window !== `undefined`) {
        localStorage.setItem(FALLBACK_LANGUAGE_STORAGE_KEY, normalized);
    }

    if (vueI18n.instance) {
        vueI18n.instance.fallbackLocale = normalized;
    }

    return fallbackLocale;
}

export function getFallbackLang() {
    return SUPPORTED_LOCALES.find(l => l.locale === fallbackLocale) || null;
}

export function getLangFromLocale(locale) {
    const normalized = normalizeLocaleCode(locale);
    if (!normalized) return null;
    const match = SUPPORTED_LOCALES.find(l => l.locale.toLowerCase() === normalized.toLowerCase());
    return match || null;
}

export function getLangsFromLocales(locales) {
    if (!Array.isArray(locales)) return [];

    const matches = [];
    const seen = new Set();

    for (let loc of locales) {
        const normalized = normalizeLocaleCode(loc);
        if (!normalized) continue;
        const key = normalized.toLowerCase();
        if (seen.has(key)) continue;
        const match = SUPPORTED_LOCALES.find(l => l.locale.toLowerCase() === key);
        if (match) {
            seen.add(key);
            matches.push(match);
        }
    }

    return matches;
}

export function getShortCodeFromFull(locale) {
    if (!locale) return null;
    return normalizeLocaleCode(locale) || String(locale).split(`-`)[0];
}

export function getFullCodeFromShort(shortLocale) {
    return normalizeLocaleCode(shortLocale);
}

export function normalizeLocaleCode(locale) {
    if (!locale) return null;

    const normalized = String(locale).replace(/_/g, `-`).trim();
    const lower = normalized.toLowerCase();

    const exact = SUPPORTED_CODES.find(code => code.toLowerCase() === lower);
    if (exact) {
        return exact;
    }

    const short = lower.split(`-`)[0];
    const shortMatch = SUPPORTED_CODES.find(code => code.toLowerCase() === short);
    return shortMatch || null;
}

// eslint-disable-next-line no-unused-vars
function initFallbackLocale() {
    if (typeof window === `undefined`) {
        return;
    }
    const stored = localStorage.getItem(FALLBACK_LANGUAGE_STORAGE_KEY);
    const normalized = normalizeLocaleCode(stored);
    if (normalized) {
        fallbackLocale = normalized;
    }
}

export function getSupportedLocales() {
    return SUPPORTED_LOCALES;
}

export function getSupportedLocalesCode() {
    return SUPPORTED_CODES;
}

export function getLocale() {
    const normalizedI18n = normalizeLocaleCode(vueI18n.instance?.locale);
    if (normalizedI18n) {
        return normalizedI18n;
    }

    // Fallback: check localStorage or browser language
    if (typeof window !== `undefined`) {
        const stored = localStorage.getItem(PREVIEW_LANGUAGE_STORAGE_KEY);
        if (stored) {
            const matched = normalizeLocaleCode(stored);
            if (matched) return matched;
        }

        // Legacy compatibility: older code still persists these keys
        const legacyStored = localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY) || localStorage.getItem(I18NEXT_LANGUAGE_STORAGE_KEY);
        if (legacyStored) {
            const matched = normalizeLocaleCode(legacyStored);
            if (matched) return matched;
        }

        if (window.defaultLanguage) {
            return window.defaultLanguage;
        }

        const browserLangRaw = navigator.language || fallbackLocale;
        const browserMatch = normalizeLocaleCode(browserLangRaw) || normalizeLocaleCode(String(browserLangRaw).split(`-`)[0]);
        if (browserMatch) return browserMatch;
    }

    return fallbackLocale;
}

export function getLocaleName() {
    const lang = getLangFromLocale(vueI18n.instance?.locale);
    if (lang) {
        return lang.name;
    } else {
        return getLocale();
    }
}

export function setLocale(locale) {
    const matched = normalizeLocaleCode(locale);
    if (!matched) return Promise.resolve(null);

    // Persist to localStorage immediately (new + legacy keys)
    if (typeof window !== `undefined`) {
        localStorage.setItem(PREVIEW_LANGUAGE_STORAGE_KEY, matched);
        localStorage.setItem(LEGACY_LANGUAGE_STORAGE_KEY, matched);
        localStorage.setItem(I18NEXT_LANGUAGE_STORAGE_KEY, matched);
    }

    if (typeof vueI18n.loadLanguageAsync === `function`) {
        return vueI18n.loadLanguageAsync(matched);
    }

    // Fallback when plugin async loader is not available
    if (vueI18n.instance) {
        vueI18n.instance.locale = matched;
    }

    return Promise.resolve(matched);
}

export function changeLanguage() {
    const codes = getSupportedLocalesCode() || [];
    const current = getLocale();
    const currentIndex = codes.findIndex(c => c.toLowerCase() === String(current || ``).toLowerCase());
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % (codes.length || 1) : 0;
    const nextLocale = codes[nextIndex] || fallbackLocale;
    setLocale(nextLocale);
    return nextLocale;
}

export function getLanguage() {
    return getShortCodeFromFull(getLocale()) || getLocale() || getShortCodeFromFull(fallbackLocale);
}

export function getLanguageObject(localeCode) {
    const locale = localeCode || getLocale();
    const normalized = normalizeLocaleCode(locale);

    if (normalized) {
        return getLangFromLocale(normalized);
    }

    return getFallbackLang();
}

export function isFrench() {
    return getShortCodeFromFull(getLocale()) === `fr`;
}

export function isEnglish() {
    return getShortCodeFromFull(getLocale()) === `en`;
}

function toShortLocale(locale) {
    if (!locale) return null;
    return getShortCodeFromFull(locale) || String(locale).split(`-`)[0];
}

function addTranslation(target, locale, value) {
    if (value === undefined || value === null) return;

    const normalizedLocale = normalizeLocaleCode(locale) || (locale ? String(locale).trim() : null);
    if (!normalizedLocale) return;
    target[normalizedLocale] = value;
}

function normalizeTranslations(translations) {
    const map = {};
    if (!translations || typeof translations !== `object`) {
        return map;
    }

    const entries = Object.entries(translations);
    const explicitKeys = new Set();

    entries.forEach(([key, value]) => {
        if (key === `default`) {
            map.default = value;
            return;
        }
        const normalized = normalizeLocaleCode(key);
        if (!normalized) return;
        if (key.toLowerCase() === normalized.toLowerCase()) {
            explicitKeys.add(normalized.toLowerCase());
            map[normalized] = value;
        }
    });

    entries.forEach(([key, value]) => {
        if (key === `default`) return;
        const normalized = normalizeLocaleCode(key);
        if (!normalized) return;
        if (key.toLowerCase() === normalized.toLowerCase()) return;
        if (explicitKeys.has(normalized.toLowerCase())) return;

        const hasProp = Object.prototype.hasOwnProperty.call(map, normalized);
        if (!hasProp || map[normalized] === `` || map[normalized] === null || map[normalized] === undefined) {
            if (value !== undefined && value !== null) {
                map[normalized] = value;
            }
        }
    });

    entries.forEach(([key, value]) => {
        if (key === `default`) return;
        const normalized = normalizeLocaleCode(key);
        if (normalized) return;
        if (value !== undefined && value !== null) {
            map[key] = value;
        }
    });

    return map;
}

// Enhanced getTranslations to handle various input formats
export function getTranslations(values) {
    if (values === null || values === undefined || values === ``) {
        return null;
    }

    if (Array.isArray(values)) {
        if (!values || values.length < 2) {
            return null;
        }
        const map = {};
        addTranslation(map, `en`, values[0]);
        addTranslation(map, `fr`, values[1]);
        return map;
    }

    if (typeof values === `object` && !Array.isArray(values)) {
        const map = normalizeTranslations(values);
        return Object.keys(map).length ? map : null;
    }

    if (typeof values === `string`) {
        try {
            const parsed = JSON.parse(values);
            if (typeof parsed === `object`) {
                return getTranslations(parsed);
            }
        } catch (e) {
            // not json
        }

        if (values.indexOf(`|`) >= 0) {
            const translations = values.split(`|`);
            const map = {};
            addTranslation(map, `en`, translations[0]);
            addTranslation(map, `fr`, translations[1]);
            return Object.keys(map).length ? map : null;
        }

        return { default: values };
    }

    return null;
}

function pickTranslation(translations, locale, shortLocale) {
    if (!translations || typeof translations !== `object`) {
        return undefined;
    }

    const normalizedMap = {};
    Object.keys(translations).forEach(key => {
        normalizedMap[String(key).toLowerCase()] = translations[key];
    });

    if (locale) {
        const value = normalizedMap[String(locale).toLowerCase()];
        if (value !== undefined) {
            return value;
        }
    }

    if (shortLocale) {
        const shortKey = String(shortLocale).toLowerCase();
        if (normalizedMap.hasOwnProperty(shortKey)) {
            return normalizedMap[shortKey];
        }
        const prefixKey = Object.keys(normalizedMap).find(k => k.startsWith(shortKey + `-`));
        if (prefixKey) {
            return normalizedMap[prefixKey];
        }
    }

    return undefined;
}

export function translate(values, locale) {
    if (values === null || values === undefined) {
        return ``;
    }

    // Handle i18n keys (e.g., 'map.instructions.walk')
    // Keys are strings with dots but no spaces, and exist in the i18n messages
    if (typeof values === `string` && values.indexOf(`.`) > -1 && !values.includes(` `)) {
        if (vueI18n.instance) {
                const translated = vueI18n.instance.t(values);
            try {
                // VueI18n returns the key itself if not found, so check if it's different
                if (translated !== values) {
                    return translated;
                }
            } catch (e) { /* empty */ }
        }
    }
    const translations = getTranslations(values);

    if (translations === null) {
        // If values is an object (empty or with no valid translations), return empty string
        // Otherwise return the original value (could be a plain string)
        return (typeof values === `object` && values !== null) ? `` : values;
    }

    let targetLocale, targetShort;
    targetLocale = normalizeLocaleCode(locale) || getLocale();
    targetShort = toShortLocale(targetLocale) || targetLocale;

    const direct = pickTranslation(translations, targetLocale, targetShort);
    if (direct !== undefined && direct !== null && direct !== ``) {
        return direct;
    }

    const fallback = pickTranslation(translations, fallbackLocale, toShortLocale(fallbackLocale));

    if (fallback !== undefined && fallback !== null && fallback !== ``) {
        return fallback;
    }

    if (translations.default !== undefined) {
        return translations.default;
    }

    const first = Object.values(translations).find(v => v !== undefined && v !== null && v !== ``);
    return first || ``;
}

export const tr = translate;

export function compareTranslation(a, b) {
    if (a && b) {
        return tr(a).localeCompare(tr(b));
    } else {
        return b - a;
    }
}

export default {
    getSupportedLocales,
    getSupportedLocalesCode,
    fallbackLocale,
    getFallbackLocale,
    setFallbackLocale,
    getLocale,
    setLocale,
    changeLanguage,
    getLanguage,
    getLanguageObject,
    getLangFromLocale,
    getLangsFromLocales,
    getFallbackLang,
    isFrench,
    isEnglish,
    normalizeLocaleCode,
    getShortCodeFromFull,
    getFullCodeFromShort,
    translate,
    tr,
    getTranslations,
    compareTranslation
};
